import { isEmpty } from "es-toolkit/compat";
import { useAtomValue, useSetAtom } from "jotai";
import type { GetStaticPaths, GetStaticProps, GetStaticPropsResult, NextPage } from "next";
import { useRouter } from "next/router";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEvent } from "react-use";
import SuperJSON from "superjson";
import { match } from "ts-pattern";
import YAML from "yaml";

import {
	type DocumentMetadata,
	DocumentMetadataSchema,
	type PageMetadata,
} from "@moment/api-collab/api-types";
import { parsePage } from "@moment/api-collab/markdown";
import type { AuthTokenResponse } from "@moment/app/auth/AuthProvider";
import { getIframeBaseUrl } from "@moment/app/auth/interop/common";
import { BigRichTextEditor } from "@moment/app/components/canvas/editor/BigRichTextEditor";
import { PublishedDocument } from "@moment/app/components/canvas/views/PublishedDocument";
import { TemplateVitePressDocs } from "@moment/app/components/canvas/views/TemplateVitePressDocs";
import { useIFrameReadyState } from "@moment/app/components/iframe/useiframeReadyState";
import { createEditorState } from "@moment/app/src/components/canvas/easel/editor-state/createEditorState";
import {
	$docsApi,
	$loadDocumentMetadata,
	$loadEditorState,
	$trackDocumentOpened,
	buildDefaultPagePath,
	editorStateKeyForDocsRoute,
	findPageBySlugPath,
	metadataKeyForDocsRoute,
	useDocumentAuth,
	useEditorState,
	useEnv,
} from "@moment/state";
import {
	LoadingSpinner,
	type MessageType,
	cn,
	generateIframeUrlPath,
	invokePostMessage,
	redirectPathWithoutParentOrigin,
	sendPostMessage,
	useIsMounted,
} from "@moment/ui";

import { PasswordDialog } from "../../../../components/PasswordDialog";

type PathParams = { workspaceName: string; documentId: string; pageSlug: string | string[] };

type DocPageProps = {
	workspaceName: string;
	documentId: string;
	pageId?: string;
	documentMetadata?: string;
	node?: string;
	passwordRequired: boolean;
	pageSlugPath: string;
};

const resolvePublishedPage = (
	metadata: DocumentMetadata,
	pageSlugPath: string
): PageMetadata | undefined => {
	const matchedPage = findPageBySlugPath(metadata.pagesMetadata, pageSlugPath);
	if (matchedPage) {
		return matchedPage;
	}

	if (pageSlugPath === "index") {
		return findPageBySlugPath(metadata.pagesMetadata, "");
	}

	return undefined;
};

const notFound = (
	error: string,
	errorContext: Record<string, unknown> = {}
): GetStaticPropsResult<DocPageProps> => {
	console.error("Page not found", { error, errorContext });
	return { notFound: true, revalidate: 30 };
};

export const getStaticProps: GetStaticProps<DocPageProps, PathParams> = async (context) => {
	try {
		if (!context.params) {
			return notFound("No params found");
		}

		const { workspaceName, documentId } = context.params;
		let slugValue = Array.isArray(context.params.pageSlug)
			? context.params.pageSlug.join("/")
			: context.params.pageSlug;

		// Strip query string if it rode along in the slug
		if (!isEmpty(slugValue)) {
			slugValue = slugValue?.split("?")[0] ?? "";
		}

		const pageSlugPath = !isEmpty(slugValue) ? (slugValue ?? "") : "index";

		if (isEmpty(workspaceName) || isEmpty(documentId)) {
			return notFound("No workspaceName or documentId found", context.params);
		}

		// Import env at runtime since it reads from process.env
		const { env } = await import("@moment/state");

		if (!env.docsApiUrl) {
			return notFound("DOCS_API_URL missing");
		}

		// Fetch metadata - 401 indicates password protection
		const metadataUrl = `${env.docsApiUrl}/api/v2/${workspaceName}/${documentId}/raw/moment.yml?ref=main`;
		const metadataRes = await fetch(metadataUrl);

		if (metadataRes.status === 401) {
			return {
				props: {
					workspaceName,
					documentId,
					passwordRequired: true,
					pageSlugPath,
				},
				revalidate: 60,
			};
		}

		if (!metadataRes.ok) {
			return notFound("Metadata fetch failed", { status: metadataRes.status });
		}

		const metadataText = await metadataRes.text();
		const metadataParsed = YAML.parse(metadataText);
		const metadataResult = DocumentMetadataSchema.safeParse(metadataParsed);
		if (!metadataResult.success) {
			return notFound("Metadata schema invalid", { error: metadataResult.error.message });
		}
		const metadata = metadataResult.data;

		const pagePath = resolvePublishedPage(metadata, pageSlugPath);
		if (!pagePath) {
			return notFound("Page not found", { pageSlugPath, metadata });
		}

		// Fetch page content, supporting both slug-based and legacy id-based storage layouts.
		const candidatePaths = [
			pagePath.path ?? buildDefaultPagePath(metadata.pagesMetadata, pagePath),
			`pages/${pagePath.id}/index.md`,
		].filter((candidate, index, candidates) => candidates.indexOf(candidate) === index);

		let markdown: string | undefined;
		let lastStatus: number | undefined;
		for (const rawPath of candidatePaths) {
			const pageUrl = `${env.docsApiUrl}/api/v2/pierre/${workspaceName}/${documentId}/file?path=${encodeURIComponent(rawPath)}&ref=main`;
			const pageRes = await fetch(pageUrl);
			if (pageRes.ok) {
				markdown = await pageRes.text();
				break;
			}

			lastStatus = pageRes.status;
			if (pageRes.status !== 404) {
				return notFound("Page content fetch failed", { status: pageRes.status, rawPath });
			}
		}

		if (markdown === undefined) {
			return notFound("Page content fetch failed", {
				status: lastStatus,
				pageId: pagePath.id,
				pageSlugPath,
			});
		}

		const { doc: node } = parsePage(markdown);

		if (!node) {
			return notFound("Page content missing", { pageSlugPath, pageId: pagePath.id });
		}

		return {
			props: {
				workspaceName,
				documentId,
				pageId: pagePath.id,
				documentMetadata: SuperJSON.stringify(metadata),
				node: SuperJSON.stringify(node),
				passwordRequired: false,
				pageSlugPath,
			},
			revalidate: 60,
		};
	} catch (error) {
		return notFound("Error fetching page", { error, context });
	}
};

export const getStaticPaths: GetStaticPaths = async () => {
	return {
		paths: [],
		fallback: "blocking",
	};
};

const CanvasIframeWithRef = forwardRef<
	HTMLIFrameElement,
	{
		workspaceName: string;
		documentId: string;
		pageId?: string;
		pageSlug?: string;
		title?: string;
	}
>(({ workspaceName, documentId, pageId, pageSlug, title }, ref) => {
	const iframeBaseUrl = getIframeBaseUrl(process.env.APP_ENV as MomentAppEnv);
	const { isReady } = useIFrameReadyState();

	const defaultIframeUrl = useMemo(() => {
		const iframeUrl = new URL(
			generateIframeUrlPath(workspaceName, documentId, pageSlug || pageId || ""),
			iframeBaseUrl
		);
		return iframeUrl.toString();
	}, [workspaceName, documentId, pageSlug, pageId, iframeBaseUrl]);

	return (
		<iframe
			ref={ref}
			src={defaultIframeUrl}
			title={title}
			className={cn(
				"fixed inset-0 w-dvw h-dvh transition-all duration-150 ease-in",
				isReady
					? "opacity-100 z-iframe pointer-events-auto"
					: "opacity-0 pointer-events-none"
			)}
		/>
	);
});
CanvasIframeWithRef.displayName = "CanvasIframeWithRef";

const handleRouterPathChange = (
	args: { path?: string; title?: string },
	displayUrlPrefix?: string
) => {
	if (!args?.path) return;

	let realPath: string = args.path;
	if (!realPath.startsWith("/")) {
		return;
	}

	// /index is redundant, remove it
	if (realPath.endsWith("/index")) {
		realPath = realPath.slice(0, -6);
	}

	if (displayUrlPrefix) {
		// Find the fourth slash in the path and remove everything before it.
		const fourthSlashIndex = realPath.split("/", 4).join("/").length;
		realPath = realPath.slice(fourthSlashIndex);
	}

	const target = `${displayUrlPrefix || window.location.origin}${realPath}`;
	try {
		window.history.pushState({}, "", target);
	} catch (error) {
		// If pushState fails (e.g. due to an invalid URL), log the error but don't crash.
		console.error("Failed to change route via pushState", { error, target });
	}
	if (args.title) {
		document.title = args.title;
	}
};

const DocumentPage: NextPage<DocPageProps> = ({
	workspaceName,
	documentId,
	pageId: initialPageId,
	documentMetadata: documentMetadataString,
	node: nodeString,
	passwordRequired,
	pageSlugPath,
}) => {
	const router = useRouter();
	const { isInIframe: envIsInIframe } = useEnv();
	const docsApi = useAtomValue($docsApi);

	// Avoid hydration mismatch - isInIframe from env returns false on server
	// but true on client when in iframe. Only use actual value after mount.
	const isMounted = useIsMounted();
	const isInIframe = isMounted && envIsInIframe;

	// Document auth for password-protected documents (parent frame only)
	const { hasToken, setTokenFromExchange, getValidToken } = useDocumentAuth(
		workspaceName,
		documentId
	);

	// Track if we're showing the password dialog due to refresh failure
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);

	// Iframe auth token state (iframe only)
	const [iframeToken, setIframeToken] = useState<string | null>(null);
	const [iframeTokenExpiresAt, setIframeTokenExpiresAt] = useState<number | null>(null);
	// Track if this is a public doc (parent confirmed no auth needed) - cacheable forever
	const [isPublicDoc, setIsPublicDoc] = useState<boolean>(false);

	// Error state for iframe auth or document load failures inside the iframe
	const [iframeError, setIframeError] = useState<string | null>(null);

	// Request auth token from parent (iframe only)
	// Returns: token string if authenticated, null if public/pending
	// Sets isPublicDoc=true if parent confirms no auth needed (cacheable)
	const requestAuthToken = useCallback(async (): Promise<{
		token: string | null;
		isPending: boolean;
	}> => {
		if (!isInIframe) return { token: null, isPending: false };

		const response = await invokePostMessage<AuthTokenResponse>("getAuthToken");

		if (!response) {
			// Legacy fallback - treat undefined as public doc
			setIsPublicDoc(true);
			return { token: null, isPending: false };
		}

		switch (response.status) {
			case "authenticated":
				setIframeToken(response.access_token);
				setIframeTokenExpiresAt(Date.now() + response.expires_in * 1000);
				return { token: response.access_token, isPending: false };
			case "unauthenticated":
				// Parent confirmed no auth needed - cache this forever
				setIsPublicDoc(true);
				return { token: null, isPending: false };
			case "pending":
				// Auth still loading on parent - should retry
				return { token: null, isPending: true };
		}
	}, [isInIframe]);

	// Get a valid token for iframe, refreshing from parent if needed
	const getIframeToken = useCallback(
		async (options?: { required?: boolean }) => {
			// If we've confirmed this is a public doc, return null immediately (no retries)
			if (isPublicDoc) {
				return null;
			}

			// Check if cached token is still valid (with 60s buffer)
			if (iframeToken && iframeTokenExpiresAt && iframeTokenExpiresAt - Date.now() > 60000) {
				return iframeToken;
			}

			// Request fresh token from parent
			const result = await requestAuthToken();

			// If token is required but pending, retry after a short delay
			if (options?.required && !result.token && result.isPending) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
				const retryResult = await requestAuthToken();
				if (options.required && !retryResult.token && !isPublicDoc) {
					setIframeError("Unable to authenticate. Please refresh the page.");
				}
				return retryResult.token;
			}

			return result.token;
		},
		[isPublicDoc, iframeToken, iframeTokenExpiresAt, requestAuthToken]
	);

	// Client-side fetched data for password-protected docs in iframe
	type ClientData = {
		metadata: DocumentMetadata;
		pageId: string;
		node: JsonObject;
		pageSlugPath: string;
		workspaceName: string;
		documentId: string;
	};
	const [clientDataCache, setClientDataCache] = useState<Record<string, ClientData>>({});
	const currentClientDataKey = useMemo(
		() => `${workspaceName}:${documentId}:${pageSlugPath}`,
		[workspaceName, documentId, pageSlugPath]
	);
	const cachedMetadata = useMemo(() => {
		const entry = Object.values(clientDataCache).find(
			(data) => data.workspaceName === workspaceName && data.documentId === documentId
		);
		return entry?.metadata ?? null;
	}, [clientDataCache, workspaceName, documentId]);
	const clientData = clientDataCache[currentClientDataKey] ?? null;
	const isIframeLoading = passwordRequired && isInIframe && !clientData;
	const emptyDoc = useMemo(
		() =>
			({
				type: "doc",
				content: [
					{
						type: "paragraph",
						content: [],
					},
				],
			}) as JsonObject,
		[]
	);

	// For password-protected docs in iframe, fetch data client-side
	useEffect(() => {
		if (!passwordRequired || !isInIframe) return;

		// Skip if we already have data for THIS specific page
		if (clientData) return;

		const fetchData = async () => {
			// Get auth token from parent (required for password-protected docs)
			const authToken = await getIframeToken({ required: passwordRequired });
			if (passwordRequired && !authToken) {
				// Error state already set by getIframeToken
				return;
			}

			// Fetch metadata (reuse cached document metadata if available)
			const metadata =
				cachedMetadata ??
				(await docsApi.document.metadata.read(
					{ workspaceName, documentId, ref: "main" },
					authToken ?? undefined
				));

			// Find page
			const pagePath = resolvePublishedPage(metadata, pageSlugPath);
			if (!pagePath) {
				console.error(`Page not found: ${pageSlugPath}`);
				setIframeError("Unable to load document");
				return;
			}

			// Fetch page content
			const resolvedPagePath =
				pagePath.path ?? buildDefaultPagePath(metadata.pagesMetadata, pagePath);
			const markdown = await docsApi.document.page.readRaw(
				{
					workspaceName,
					documentId,
					pageId: pagePath.id,
					pagePath: resolvedPagePath,
					ref: "main",
				},
				authToken ?? undefined
			);
			const { doc } = parsePage(markdown);

			setClientDataCache((prev) => {
				if (prev[currentClientDataKey]) return prev;
				return {
					...prev,
					[currentClientDataKey]: {
						metadata,
						pageId: pagePath.id,
						node: doc,
						pageSlugPath,
						workspaceName,
						documentId,
					},
				};
			});
		};

		fetchData().catch((e) => {
			console.error("Failed to fetch document data:", e);
			setIframeError("Unable to load document");
		});
	}, [
		passwordRequired,
		isInIframe,
		clientData,
		cachedMetadata,
		currentClientDataKey,
		pageSlugPath,
		docsApi,
		workspaceName,
		documentId,
		getIframeToken,
	]);

	// Handle password submission (parent frame only)
	const handlePasswordSubmit = useCallback(
		async (password: string) => {
			const result = await docsApi.document.publish.exchangePasswordForToken({
				workspaceName,
				documentId,
				password,
			});
			setTokenFromExchange(result);
			setShowPasswordDialog(false);
		},
		[docsApi, workspaceName, documentId, setTokenFromExchange]
	);

	// Periodically check token validity and trigger re-auth if needed (parent only)
	useEffect(() => {
		if (!hasToken || isInIframe || !passwordRequired) return;

		const checkToken = async () => {
			const result = await getValidToken();
			if (!result.success) {
				// Token refresh failed - show password dialog again
				setShowPasswordDialog(true);
			}
		};

		// Check immediately
		void checkToken();

		// Check every 30 seconds
		const interval = setInterval(() => void checkToken(), 30 * 1000);
		return () => clearInterval(interval);
	}, [hasToken, isInIframe, passwordRequired, getValidToken]);

	// Resolve data - use client-fetched data for password-protected iframe, otherwise SSR data
	const documentMetadata =
		clientData?.metadata ??
		cachedMetadata ??
		(documentMetadataString ? SuperJSON.parse<DocumentMetadata>(documentMetadataString) : null);
	const resolvedPageMetadata = useMemo(() => {
		if (!documentMetadata) return null;
		return resolvePublishedPage(documentMetadata, pageSlugPath) ?? null;
	}, [documentMetadata, pageSlugPath]);
	const pageId = clientData?.pageId ?? initialPageId ?? resolvedPageMetadata?.id ?? null;
	const nodeFromProps =
		clientData?.node ?? (nodeString ? SuperJSON.parse<JsonObject>(nodeString) : null);
	const node = nodeFromProps ?? (isIframeLoading && documentMetadata && pageId ? emptyDoc : null);
	const pageMetadata =
		documentMetadata?.pagesMetadata.find((page) => page.id === pageId) ??
		resolvedPageMetadata ??
		null;
	const displayUrlPrefix = documentMetadata?.publishSettings?.urlPrefix;

	// Track document open analytics — skip in iframe to avoid double-counting
	// (parent renders CanvasIframeWithRef which loads this same route in an iframe)
	const trackDocumentOpened = useSetAtom($trackDocumentOpened);
	const lastTrackedDocRef = useRef<string | null>(null);
	useEffect(() => {
		if (envIsInIframe) return;
		if (lastTrackedDocRef.current !== documentId) {
			lastTrackedDocRef.current = documentId;
			trackDocumentOpened({ documentId, workspaceName });
		}
	}, [trackDocumentOpened, documentId, workspaceName, envIsInIframe]);
	useEffect(() => {
		if (!isInIframe) {
			return;
		}

		const path = redirectPathWithoutParentOrigin();
		sendPostMessage("routerPathChange", {
			path,
			title: documentMetadata?.title || document.title,
		});
	}, [isInIframe, router.asPath, documentMetadata?.title]);
	// These hooks must be called unconditionally (before any returns)
	const metadataKey = useMemo(() => metadataKeyForDocsRoute(documentId), [documentId]);
	const loadMetadata = useSetAtom($loadDocumentMetadata);
	const loadEditorState = useSetAtom($loadEditorState);
	const { editorState, editorStateKey } = useEditorState(documentId, pageId);

	// Load metadata into store SYNCHRONOUSLY during render (not in useEffect).
	// This is critical because child components (BigRichTextEditor -> Editor -> useComputeCells)
	// depend on $documentMetadata being available via usePageFromCurrentRoute() to get the correct
	// pageId for mounting $compilationEffect. If we load metadata in useEffect, the first render
	// will have undefined pageId, causing a key mismatch with the editor state.
	const metadataLoadedRef = useRef(false);
	if (documentId && documentMetadata && !metadataLoadedRef.current) {
		metadataLoadedRef.current = true;
		loadMetadata({
			key: metadataKey,
			metadata: documentMetadata,
			ssr: true,
		});
	}

	// Create editor state (may be null if data not loaded yet)
	const ssrEditorState = useMemo(() => {
		if (!documentMetadata || !node || !pageId) return null;

		return createEditorState({
			documentId,
			pageId,
			doc: node,
			ssr: true,
		});
	}, [documentMetadata, node, pageId, documentId]);

	// Listen for iframe -> parent messages
	useEvent("message", (event) => {
		//router path change messages are only need to update in the parent
		if (isInIframe) return;

		const data = event.data as { type?: MessageType; args?: { path?: string; title?: string } };
		if (!data || data.type !== "routerPathChange") {
			return;
		}

		handleRouterPathChange(data.args || {}, displayUrlPrefix);
	});

	// Hydrate editor state only inside iframe
	useEffect(() => {
		if (!isInIframe || !ssrEditorState || !editorStateKey || isIframeLoading || !!editorState)
			return;

		void loadEditorState({
			key: editorStateKey,
			doc: node,
			plugins: [...ssrEditorState.plugins],
			ssr: false,
			onPersist: async () => {},
		});
	}, [isInIframe, loadEditorState, ssrEditorState, editorStateKey, isIframeLoading, editorState]);

	// Password-protected document flow (parent frame only)
	if (passwordRequired && !isInIframe) {
		// No token yet OR refresh failed - show password dialog
		if (!hasToken || showPasswordDialog) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-primary">
					<PasswordDialog isOpen={true} onSubmit={handlePasswordSubmit} />
				</div>
			);
		}

		// Have token - render iframe (token will be requested via postMessage)
		return (
			<>
				<div className="flex justify-center items-center fixed inset-0">
					<LoadingSpinner />
				</div>

				<CanvasIframeWithRef
					workspaceName={workspaceName}
					documentId={documentId}
					pageSlug={pageSlugPath}
				/>
			</>
		);
	}

	// Password-protected iframe flow - show error if auth or document load failed
	if (iframeError) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-primary">
				<div className="text-secondary">{iframeError}</div>
			</div>
		);
	}

	// Password-protected iframe flow - wait for client data
	if (isIframeLoading && !documentMetadata) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-primary">
				<LoadingSpinner />
			</div>
		);
	}

	// Safety check - data not loaded yet
	if (!documentMetadata || !node || !pageId || !ssrEditorState) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-primary">
				<div className="text-secondary">Unable to load document</div>
			</div>
		);
	}

	return (
		<>
			<div className="min-h-screen relative flex flex-col bg-primary">
				<BigRichTextEditor
					document={documentMetadata}
					editorState={editorState || ssrEditorState}
					documentInteractionMode="static"
				>
					{match(documentMetadata.publishSettings?.template)
						.with("blog", () => (
							<PublishedDocument
								canvas={documentMetadata}
								pageId={pageId}
								pageMetadata={pageMetadata}
							/>
						))
						.otherwise(() => (
							<TemplateVitePressDocs
								canvas={documentMetadata}
								loading={isIframeLoading}
							/>
						))}
				</BigRichTextEditor>

				{/* Only render iframe if mounted and not in iframe environment, without the mount check React throws hydration errors */}
				{isMounted && !envIsInIframe && (
					<CanvasIframeWithRef
						workspaceName={workspaceName}
						documentId={documentId}
						pageId={pageId}
						pageSlug={pageSlugPath}
						title={documentMetadata?.title}
					/>
				)}
			</div>
		</>
	);
};

export default DocumentPage;
